import { Component, Inject, OnInit } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { NgxTaskService } from '@go-modules/services/task/task.service';
import * as Papa from 'papaparse';
import { catchError, finalize, of, combineLatest, take } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { LoadingModule } from '../loading/loading.module';
import { Task } from '@go-modules/interfaces/task';
import { Activity } from '@go-modules/interfaces/activity';
import { OverviewNavigationComponent } from '@go-modules/components/overview-navigation/overview-navigation.component';

export interface TaskReportRow {
	userId: string;
	firstName: string;
	lastName: string;
	learnerName: string;
	taskCompletions: boolean[];
	totalCompleted: number;
}

export interface TaskReportData {
	headers: string[];
	rows: TaskReportRow[];
	taskNames: string[];
	taskTitles: string[];
}

@Component({
	selector: 'ngx-task-report',
	standalone: true,
	imports: [
		CommonModule,
		TranslateModule,
		AsyncPipe,
		LoadingModule,
		OverviewNavigationComponent
	],
	templateUrl: './task-report.component.html',
	styleUrls: ['./task-report.component.scss']
})
export class NgxTaskReportComponent implements OnInit {
	public activityId: number | null = null;
	public isLoading$ = new BehaviorSubject<boolean>(true);
	public error$ = new BehaviorSubject<string | null>(null);
	public reportData$ = new BehaviorSubject<TaskReportData | null>(null);

	constructor (
		@Inject(selectedServiceToken) private readonly selectedService: SelectedService,
		private readonly activityService: NgxActivityService,
		private readonly taskService: NgxTaskService,
		private readonly translate: TranslateService
	) {
		this.activityId = this.selectedService.getActivity().activity_id;
	}

	public ngOnInit (): void {
		this.loadReportData();
	}

	public getRowAriaLabel (row: TaskReportRow): string {
		let label = '';
		const taskTitles = this.reportData$.value.taskTitles;
		label += this.translate.instant('task-report_row-aria-label', {
			learnerName: row.learnerName,
			totalCompleted: row.totalCompleted,
			totalTasks: this.reportData$.value.taskNames.length
		});
		row.taskCompletions.forEach((isCompleted, index) => {
			const taskTitle = taskTitles[index];
			if (taskTitle) {
				label += ', ';
				label += taskTitle;
				label += ' ';
				if (isCompleted) {
					label += this.translate.instant('common_completed').toLowerCase();
				} else {
					label += this.translate.instant('common_incomplete').toLowerCase();
				}
			}
		});
		return label;
	}

	private loadReportData (): void {
		this.isLoading$.next(true);
		this.error$.next(null);

		const activity = this.selectedService.getActivity();
		combineLatest([
			this.activityService.getTaskCompletionReport(this.activityId).pipe(take(1)),
			this.taskService.getTasks(this.activityId).pipe(take(1))
		])
			.pipe(
				catchError(() => {
					this.error$.next('Failed to load task completion report');
					return of([null as string | null, [] as Task[]] as [string | null, Task[]]);
				}),
				finalize(() => {
					this.isLoading$.next(false);
				})
			)
			.subscribe(([csvText, tasks]) => {
				if (csvText === null || typeof csvText !== 'string') {
					return;
				}

				try {
					const parsed = this.parseCsvData(csvText, tasks, activity);
					this.reportData$.next(parsed);
				} catch (error) {
					this.error$.next('Failed to parse task completion report');
				}
			});
	}

	private parseCsvData (csvText: string, tasks: Task[], activity: Activity | null): TaskReportData {
		const result = Papa.parse<string[]>(csvText, {
			skipEmptyLines: false,
			quoteChar: '"',
			escapeChar: '"',
			header: false
		});
		const allRows = result.data as string[][];

		// Find header row (format is consistent but may have metadata lines before header)
		let headerRowIndex = -1;
		for (let i = 0; i < allRows.length; i++) {
			if (allRows[i].includes('User ID')) {
				headerRowIndex = i;
				break;
			}
		}

		if (headerRowIndex === -1) {
			return { headers: [], rows: [], taskNames: [], taskTitles: [] };
		}

		const headers = allRows[headerRowIndex];
		const dataRows = allRows.slice(headerRowIndex + 1);

		// Format is always: User ID (0), First Name (1), Last Name (2), then tasks (3+)
		const USER_ID_INDEX = 0;
		const FIRST_NAME_INDEX = 1;
		const LAST_NAME_INDEX = 2;
		const TASKS_START_INDEX = 3;

		const taskNames = headers.slice(TASKS_START_INDEX);
		const sortedTasks = tasks
			.filter((task) => task.deleted_at == null)
			.sort((a, b) => a.sort_order - b.sort_order);

		const taskTitles = taskNames.map((taskName, index) => {
			const matchingTask = sortedTasks[index];
			if (matchingTask && activity) {
				return this.activityService.getTaskTitleAndDescription(activity, matchingTask, true).title;
			}
			return taskName;
		});

		const parsedRows: TaskReportRow[] = dataRows
			.filter((row) => row.length > 0 && row[USER_ID_INDEX])
			.map((row) => {
				const userId = row[USER_ID_INDEX];
				const firstName = row[FIRST_NAME_INDEX] || '';
				const lastName = row[LAST_NAME_INDEX] || '';
				const learnerName = `${firstName} ${lastName}`.trim();

				const taskCompletions: boolean[] = [];
				let totalCompleted = 0;

				for (let index = 0; index < taskNames.length; index++) {
					const taskIndex = TASKS_START_INDEX + index;
					const completionValue = row[taskIndex]?.toLowerCase().trim();
					const isCompleted = completionValue === 'true' || completionValue === '1';
					taskCompletions.push(isCompleted);
					if (isCompleted) {
						totalCompleted++;
					}
				}

				return {
					userId,
					firstName,
					lastName,
					learnerName,
					taskCompletions,
					totalCompleted
				};
			});

		return {
			headers,
			rows: parsedRows,
			taskNames,
			taskTitles
		};
	}
}
